import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ClipboardList, BookOpen, FileSearch, Paperclip, AlertOctagon, FileText, GanttChart, Shield, ScrollText, UserCircle, LogOut, Menu, ShieldCheck, ShieldAlert, CalendarDays } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { useI18n, ROLES } from "@/lib/i18n";

function useNav() {
  const { tx } = useI18n();
  const { user } = useAuth();
  const main = [
    { to: "/", icon: LayoutDashboard, label: tx("Tableau de bord", "Dashboard"), id: "dashboard", end: true },
    { to: "/clients", icon: Building2, label: tx("Clients", "Clients"), id: "clients" },
    { to: "/audits", icon: ClipboardList, label: tx("Audits", "Audits"), id: "audits" },
    { to: "/questionnaires", icon: BookOpen, label: tx("Questionnaires", "Questionnaires"), id: "questionnaires" },
    { to: "/m/evidence", icon: Paperclip, label: tx("Preuves", "Evidence library"), id: "evidence" },
    { to: "/m/documentary", icon: FileSearch, label: tx("Contrôles documentaires", "Documentary checks"), id: "documentary" },
    { to: "/m/findings", icon: AlertOctagon, label: tx("Constats & risques", "Findings & risks"), id: "findings" },
    { to: "/m/actions", icon: GanttChart, label: tx("Plan d'action & Gantt", "Action plan & Gantt"), id: "actions" },
    { to: "/planning", icon: CalendarDays, label: tx("Planning", "Planning"), id: "planning" },
    { to: "/reports", icon: FileText, label: tx("Rapports", "Reports"), id: "reports" },
  ];
  const later = [
  ];
  const admin = [];
  if (user?.platform_role === "admin") admin.push({ to: "/admin", icon: Shield, label: tx("Administration", "Admin"), id: "admin" });
  if (["admin", "lead"].includes(user?.platform_role)) admin.push({ to: "/logs", icon: ScrollText, label: tx("Journaux d'accès", "Access logs"), id: "logs" });
  admin.push({ to: "/account", icon: UserCircle, label: tx("Mon compte & MFA", "Account & MFA"), id: "account" });
  return { main, later, admin };
}

function NavItems({ onClick }) {
  const { tx } = useI18n();
  const { main, later, admin } = useNav();
  const item = (n) => (
    <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick} data-testid={`nav-${n.id}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-[#2176AE] text-white" : "text-slate-200 hover:bg-white/10"}`}>
      <n.icon className="h-4 w-4 shrink-0" /> <span className="truncate">{n.label}</span>
    </NavLink>
  );
  return (
    <nav className="flex flex-col gap-1">
      {main.map(item)}
      {later.length > 0 && <div className="mt-5 mb-1 px-3 text-[10px] uppercase tracking-[0.18em] text-slate-400">{tx("À venir (non implémenté)", "Upcoming (not implemented)")}</div>}
      {later.map(item)}
      <div className="mt-5 mb-1 px-3 text-[10px] uppercase tracking-[0.18em] text-slate-400">{tx("Gouvernance", "Governance")}</div>
      {admin.map(item)}
    </nav>
  );
}

const Logo = () => (
  <div className="px-3 py-1">
    <img data-testid="app-logo" src="/motravay-logo-white.png" alt="Motravay" className="h-6 w-auto" />
    <div className="text-[11px] uppercase tracking-[0.2em] text-sky-200">HR Audit 360°</div>
  </div>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const { lang, setLang, tx, lbl } = useI18n();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col gap-6 bg-[#153A63] p-4 sticky top-0 h-screen overflow-y-auto">
        <Logo />
        <NavItems />
        <div className="mt-auto px-3 text-[11px] text-slate-400">{tx("Usage interne Motravay uniquement", "Motravay internal use only")}</div>
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-[#153A63] border-none p-4 flex flex-col gap-6">
          <Logo />
          <NavItems onClick={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/90 backdrop-blur-md px-4 sm:px-6 h-14">
          <button className="lg:hidden p-2 -ml-2" aria-label="Menu" data-testid="mobile-menu-btn" onClick={() => setOpen(true)}><Menu className="h-5 w-5 text-[#153A63]" /></button>
          <span data-testid="demo-badge" className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold tracking-wider text-amber-800">DEMO DATA</span>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs font-semibold" role="group" aria-label="Language">
              {["fr", "en"].map((l) => (
                <button key={l} data-testid={`lang-switch-${l}`} aria-pressed={lang === l} onClick={() => setLang(l)}
                  className={`px-2.5 py-1 transition-colors ${lang === l ? "bg-[#153A63] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button onClick={() => nav("/account")} data-testid="topbar-account" className="hidden sm:flex items-center gap-2 text-left">
              {user?.mfa_enabled ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-amber-600" />}
              <div className="leading-tight">
                <div className="text-sm font-medium text-slate-800" data-testid="topbar-user-name">{user?.name}</div>
                <div className="text-[11px] text-slate-500">{lbl(ROLES, user?.platform_role)}</div>
              </div>
            </button>
            <button data-testid="logout-btn" onClick={logout} className="p-2 rounded-md hover:bg-slate-100" aria-label={tx("Déconnexion", "Log out")}><LogOut className="h-4 w-4 text-slate-600" /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
